import { SectionCard } from '@/components/SectionCard'
import { Button } from '@/components/ui/button'
import { Bed } from '@/features/beds/types/bed'
import { ItemStats } from '@/features/items/components/ItemStats'
import { PatientItems } from '@/features/patients/types/patient'
import { Bed as BedIcon, Monitor, UserIcon } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { PatientCard } from './PatientCard'
import { BedToPatientSection } from './sections/BedToPatientSection'
import { PatientItemsSection } from './sections/PatientItemsSection'

export function PatientDetailContent({ patientItems }: { patientItems: PatientItems }) {
  const { t } = useTranslation()

  const [activeSection, setActiveSection] = useState<'manageBed' | 'manageItem' | null>(null)
  const patient = patientItems?.patient
  const items = patientItems?.items ?? []
  const hasBed = !!patient?.bedId
  const hasItems = items.length > 0
  const bed: Bed | undefined = patient.bedId
    ? {
        id: patient.bedId ?? '',
        name: patient.bedName ?? '',
        departmentId: patient.departmentId ?? '',
        departmentName: patient.departmentName ?? '',
        patientId: patient.bedId ?? '',
        patientName: patient.fullName ?? '',
      }
    : undefined
  return (
    <>
      <SectionCard noCardTitle>
        <div className="flex flex-col gap-5">
          <PatientCard patient={patient} variant="summary" isLoading={false}></PatientCard>

          {!hasBed && !hasItems ? (
            <p className="py-8 text-center text-xs text-muted-foreground" data-testid="patient-detail-no-bed-equipment">
              {t('patient.detail.noBedAndEquipment')}
            </p>
          ) : (
            <>
              {hasBed ? (
                <>
                  <div className="text-sm mb-1">{t('common.bed')}</div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 rounded-lg border border-border/30 bg-secondary/30 p-3">
                    <div className="flex flex-row items-center gap-4">
                      <BedIcon className="size-4 text-primary" />
                      <div className="flex flex-col gap-2">
                        <span className="text-sm font-semibold text-foreground">{patient?.bedName}</span>
                        <span className="text-sm  text-muted-foreground">
                          {t('common.department')}: {patient.departmentName}
                        </span>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <p className="py-8 text-center text-xs text-muted-foreground">{t('patient.detail.noBed')}</p>
              )}
              {hasItems ? (
                <>
                  <div className="text-sm mb-1">{t('common.equipment')}</div>
                  <div className="flex flex-col gap-3" data-testid="patient-detail-equipment-list">
                    {items.map(item => {
                      const title = item.productName || `${item.productBrand} ${item.productModel}`
                      return (
                        <ItemStats
                          key={item.id}
                          title={title}
                          subtitle={item.itemSerialNumber}
                          // status={item.status}
                          days={item.daysSinceLastStatusChange}
                        ></ItemStats>
                      )
                    })}
                  </div>
                </>
              ) : (
                <p className="py-8 text-center text-xs text-muted-foreground">{t('bed.detail.noEquipment')}</p>
              )}
            </>
          )}
        </div>
      </SectionCard>

      {/* Action Buttons */}
      <div className="mt-4 grid grid-cols-2 gap-2">
        <Button
          variant={activeSection === 'manageBed' ? 'default' : 'outline'}
          className="h-12 flex-col gap-1 text-xs"
          data-testid="patient-detail-manage-bed"
          onClick={() => setActiveSection(activeSection === 'manageBed' ? null : 'manageBed')}
        >
          <UserIcon className="size-4" />
          {t('patient.detail.manageBed')}
        </Button>
        <Button
          variant={activeSection === 'manageItem' ? 'default' : 'outline'}
          className="h-12 flex-col gap-1 text-xs"
          data-testid="patient-detail-manage-equipment"
          onClick={() => setActiveSection(activeSection === 'manageItem' ? null : 'manageItem')}
        >
          <Monitor className="size-4" />
          {t('patient.detail.manageEquipment')}
        </Button>
      </div>

      {/* Sections */}
      {activeSection === 'manageBed' && (
        <BedToPatientSection patientId={patient.id} departmentId={patient.departmentId} bed={bed}></BedToPatientSection>
      )}
      {activeSection === 'manageItem' && (
        <PatientItemsSection departmentId={patient?.departmentId} patientItems={patientItems} testIdPrefix="patient-detail"></PatientItemsSection>
      )}
    </>
  )
}
